import { useCallback, useMemo, useState } from 'react';
import type { AppLanguage } from '../i18n/types';
import type { MessageKey } from '../i18n/messages';
import type { TranslateParams } from '../i18n/translate';
import {
  buildMonthCalendarGrid,
  formatCalendarDayKeyLabel,
  parseCalendarDayKey,
  todayCalendarDayKey,
} from '../utils/assetBookingCalendar';
import {
  bookingHourSpanOnDay,
  bookingsOnDay,
  countBookingsPerDay,
  ownerDayTimelineHours,
  resolveOwnerCalendarDayAppearance,
  type OwnerCalendarDayAppearance,
  type OwnerCalendarViewMode,
  shiftCalendarDayKey,
  shiftMonth,
  weekDayKeysForAnchor,
} from '../utils/assetOwnerBookingCalendar';
import {
  bookingRecordDateRangeLabel,
  bookingRecordIsHourly,
  bookingRecordScheduleLabel,
} from '../utils/bookingListDisplay';
import { bookingRecordBookerLabel, bookingRecordStatusDisplay, bookerLabelToInitials } from '../utils/bookingRecordBooker';
import { bookingRecordIsTerminal } from '../utils/bookingRecordMutate';
import { bookingCalendarEventFromRecord } from '../utils/bookingCalendarExport';
import { BookingCalendarExportButtons } from './BookingCalendarExportButtons';
import styles from './AssetOwnerBookingCalendar.module.css';

type TranslateFn = (key: MessageKey, params?: TranslateParams) => string;

export type AssetOwnerBookingCalendarProps = {
  bookings: Record<string, unknown>[];
  assetRecord?: Record<string, unknown> | null;
  assetName?: string | null;
  assetAddress?: string | null;
  loading?: boolean;
  error?: string | null;
  language: AppLanguage;
  theme?: 'dark' | 'light';
  t: TranslateFn;
  onRetry?: () => void;
};

const APPEARANCE_CLASS: Record<OwnerCalendarDayAppearance, string> = {
  blackout: styles.monthCellBlackout,
  closed: styles.monthCellClosed,
  openWithBookings: styles.monthCellOpenWithBookings,
  openEmpty: styles.monthCellOpenEmpty,
};

function weekdayShortLabels(language: AppLanguage): string[] {
  const locale = language === 'he' ? 'he-IL' : undefined;
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, index) => fmt.format(new Date(2024, 0, 7 + index)));
}

function bookingScheduleLabel(
  booking: Record<string, unknown>,
  language: AppLanguage,
): string {
  if (bookingRecordIsHourly(booking)) {
    return bookingRecordScheduleLabel(booking, language);
  }
  return bookingRecordDateRangeLabel(booking, language);
}

function periodLabelForView(
  view: OwnerCalendarViewMode,
  anchorDayKey: string,
  viewYear: number,
  viewMonthIndex: number,
  language: AppLanguage,
): string {
  if (view === 'month') {
    return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, {
      month: 'long',
      year: 'numeric',
    }).format(new Date(viewYear, viewMonthIndex, 1));
  }
  if (view === 'day') {
    return formatCalendarDayKeyLabel(anchorDayKey, language);
  }
  const weekKeys = weekDayKeysForAnchor(anchorDayKey);
  const first = weekKeys[0];
  const last = weekKeys[weekKeys.length - 1];
  if (!first || !last) {
    return formatCalendarDayKeyLabel(anchorDayKey, language);
  }
  return `${formatCalendarDayKeyLabel(first, language)} – ${formatCalendarDayKeyLabel(last, language)}`;
}

function formatHourLabel(hour: number, language: AppLanguage): string {
  const dt = new Date(2024, 0, 1, hour, 0, 0, 0);
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(dt);
}

function EmptyDayDash() {
  return <p className={styles.emptyDash}>—</p>;
}

function BookingCards({
  bookings,
  language,
  t,
  assetName,
  assetAddress,
}: {
  bookings: Record<string, unknown>[];
  language: AppLanguage;
  t: TranslateFn;
  assetName?: string | null;
  assetAddress?: string | null;
}) {
  if (bookings.length === 0) {
    return <EmptyDayDash />;
  }

  return (
    <ul className={styles.bookingList}>
      {bookings.map((booking, index) => {
        const booker = bookingRecordBookerLabel(booking);
        const status = bookingRecordStatusDisplay(booking);
        const schedule = bookingScheduleLabel(booking, language);
        const people =
          typeof booking.amount_of_people === 'number' && Number.isFinite(booking.amount_of_people)
            ? booking.amount_of_people
            : null;
        const terminal = bookingRecordIsTerminal(booking);
        const cardClass = [styles.bookingCard, terminal ? styles.bookingCardTerminal : '']
          .filter(Boolean)
          .join(' ');
        const calendarEvent = bookingCalendarEventFromRecord(booking, {
          assetName,
          assetAddress,
          language,
        });

        return (
          <li
            key={String(booking.id ?? booking.booking_id ?? `booking-${index}`)}
            className={cardClass}
          >
            <div className={styles.bookingCardHeader}>
              <p className={styles.bookerName}>
                {booker || t('assets.ownerSchedule.unknownBooker')}
              </p>
              {status ? <span className={styles.bookingStatus}>{status}</span> : null}
            </div>
            <p className={styles.bookingSchedule}>{schedule}</p>
            {people !== null ? (
              <p className={styles.bookingMeta}>
                {t('assets.ownerSchedule.peopleCount', { n: people })}
              </p>
            ) : null}
            <BookingCalendarExportButtons event={calendarEvent} variant="owner" />
          </li>
        );
      })}
    </ul>
  );
}

function DayHourlyTimeline({
  assetRecord,
  dayKey,
  bookings,
  language,
  t,
}: {
  assetRecord: Record<string, unknown> | null | undefined;
  dayKey: string;
  bookings: Record<string, unknown>[];
  language: AppLanguage;
  t: TranslateFn;
}) {
  const hours = useMemo(() => ownerDayTimelineHours(assetRecord, dayKey), [assetRecord, dayKey]);
  const timelineStart = hours[0] ?? 0;
  const timelineEnd = (hours[hours.length - 1] ?? 23) + 1;
  const spanHours = Math.max(1, timelineEnd - timelineStart);

  if (hours.length === 0) {
    return <p className={styles.timelineClosedHint}>{t('assets.ownerSchedule.dayClosed')}</p>;
  }

  return (
    <div className={styles.dayTimeline}>
      <div className={styles.dayTimelineHours} aria-hidden>
        {hours.map((hour) => (
          <div key={hour} className={styles.dayTimelineHourLabel}>
            {formatHourLabel(hour, language)}
          </div>
        ))}
      </div>
      <div
        className={styles.dayTimelineTrack}
        style={{ ['--timeline-hours' as string]: String(spanHours) }}
      >
        {hours.map((hour) => (
          <div key={`grid-${hour}`} className={styles.dayTimelineGridLine} />
        ))}
        {bookings.map((booking, index) => {
          const span = bookingHourSpanOnDay(booking, dayKey);
          if (!span) {
            return null;
          }
          const startFrac = span.startHour + span.startMinute / 60;
          const endFrac = span.endHour + span.endMinute / 60;
          const topPct = ((startFrac - timelineStart) / spanHours) * 100;
          const heightPct = Math.max(4, ((endFrac - startFrac) / spanHours) * 100);
          const booker = bookingRecordBookerLabel(booking);
          const schedule = bookingScheduleLabel(booking, language);
          const terminal = bookingRecordIsTerminal(booking);
          return (
            <div
              key={String(booking.id ?? booking.booking_id ?? `timeline-${index}`)}
              className={[
                styles.dayTimelineBooking,
                terminal ? styles.dayTimelineBookingTerminal : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ top: `${topPct}%`, height: `${heightPct}%` }}
              title={booker ? `${booker} · ${schedule}` : schedule}
            >
              <span className={styles.dayTimelineBookingLabel}>
                {booker || t('assets.ownerSchedule.unknownBooker')}
              </span>
              <span className={styles.dayTimelineBookingTime}>{schedule}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekDayColumn({
  dayKey,
  bookings,
  assetRecord,
  language,
  t,
  onSelectDay,
}: {
  dayKey: string;
  bookings: Record<string, unknown>[];
  assetRecord: Record<string, unknown> | null | undefined;
  language: AppLanguage;
  t: TranslateFn;
  onSelectDay: (dayKey: string) => void;
}) {
  const dayNumber = parseCalendarDayKey(dayKey)?.day ?? dayKey.slice(-2);
  const appearance = resolveOwnerCalendarDayAppearance(assetRecord, dayKey, bookings.length);
  const columnClass = [
    styles.weekDayColumn,
    APPEARANCE_CLASS[appearance],
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={columnClass}
      onClick={() => onSelectDay(dayKey)}
      aria-label={formatCalendarDayKeyLabel(dayKey, language)}
    >
      <div className={styles.weekDayHeader}>
        <span className={styles.weekDayDate}>{dayNumber}</span>
      </div>
      {bookings.length === 0 ? (
        <EmptyDayDash />
      ) : (
        <ul className={styles.weekBookingList}>
          {bookings.map((booking, index) => {
            const booker = bookingRecordBookerLabel(booking);
            const initials = bookerLabelToInitials(booker);
            const schedule = bookingScheduleLabel(booking, language);
            const terminal = bookingRecordIsTerminal(booking);
            const title = booker
              ? `${booker} · ${schedule}`
              : `${t('assets.ownerSchedule.unknownBooker')} · ${schedule}`;
            return (
              <li
                key={String(booking.id ?? booking.booking_id ?? `week-booking-${index}`)}
                className={[
                  styles.weekBookingInitials,
                  terminal ? styles.weekBookingInitialsTerminal : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title={title}
              >
                {initials}
              </li>
            );
          })}
        </ul>
      )}
    </button>
  );
}

function CalendarLegend({ t }: { t: TranslateFn }) {
  return (
    <ul className={styles.legend} aria-label={t('assets.ownerSchedule.legendTitle')}>
      <li className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles.legendSwatchClosed}`} aria-hidden />
        {t('assets.ownerSchedule.legendClosed')}
      </li>
      <li className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles.legendSwatchOpenEmpty}`} aria-hidden />
        {t('assets.ownerSchedule.legendOpenEmpty')}
      </li>
      <li className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles.legendSwatchOpenBookings}`} aria-hidden />
        {t('assets.ownerSchedule.legendOpenBookings')}
      </li>
      <li className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles.legendSwatchBlackout}`} aria-hidden />
        {t('assets.ownerSchedule.legendBlackout')}
      </li>
    </ul>
  );
}

export function AssetOwnerBookingCalendar({
  bookings,
  assetRecord = null,
  assetName = null,
  assetAddress = null,
  loading = false,
  error = null,
  language,
  theme = 'dark',
  t,
  onRetry,
}: AssetOwnerBookingCalendarProps) {
  const todayKey = todayCalendarDayKey();
  const [view, setView] = useState<OwnerCalendarViewMode>('month');
  const [anchorDayKey, setAnchorDayKey] = useState(todayKey);
  const [selectedDayKey, setSelectedDayKey] = useState(todayKey);
  const [viewYear, setViewYear] = useState(() => {
    const year = Number(todayKey.slice(0, 4));
    return Number.isFinite(year) ? year : new Date().getFullYear();
  });
  const [viewMonthIndex, setViewMonthIndex] = useState(() => {
    const month = Number(todayKey.slice(5, 7)) - 1;
    return Number.isFinite(month) ? month : new Date().getMonth();
  });

  const weekdays = useMemo(() => weekdayShortLabels(language), [language]);
  const monthCells = useMemo(
    () => buildMonthCalendarGrid(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex],
  );
  const monthCellsToRender = useMemo(() => {
    // `buildMonthCalendarGrid` renders a fixed 6-week grid; drop any trailing
    // week rows that are entirely outside the current month.
    let cells = monthCells;
    while (cells.length >= 7) {
      const lastRow = cells.slice(-7);
      if (lastRow.every((c) => !c.inMonth)) {
        cells = cells.slice(0, -7);
        continue;
      }
      break;
    }
    return cells;
  }, [monthCells]);
  const monthCounts = useMemo(
    () => countBookingsPerDay(bookings, monthCellsToRender.map((cell) => cell.dayKey)),
    [bookings, monthCellsToRender],
  );
  const weekKeys = useMemo(() => weekDayKeysForAnchor(anchorDayKey), [anchorDayKey]);

  const periodLabel = periodLabelForView(view, anchorDayKey, viewYear, viewMonthIndex, language);
  const rootClass = [styles.ownerScheduleRoot, theme === 'light' ? styles.lightTheme : '']
    .filter(Boolean)
    .join(' ');

  const onPrev = () => {
    if (view === 'month') {
      const next = shiftMonth(viewYear, viewMonthIndex, -1);
      setViewYear(next.year);
      setViewMonthIndex(next.monthIndex);
      return;
    }
    const delta = view === 'week' ? -7 : -1;
    const nextKey = shiftCalendarDayKey(anchorDayKey, delta);
    if (nextKey) {
      setAnchorDayKey(nextKey);
      setSelectedDayKey(nextKey);
      const year = Number(nextKey.slice(0, 4));
      const month = Number(nextKey.slice(5, 7)) - 1;
      if (Number.isFinite(year) && Number.isFinite(month)) {
        setViewYear(year);
        setViewMonthIndex(month);
      }
    }
  };

  const onNext = () => {
    if (view === 'month') {
      const next = shiftMonth(viewYear, viewMonthIndex, 1);
      setViewYear(next.year);
      setViewMonthIndex(next.monthIndex);
      return;
    }
    const delta = view === 'week' ? 7 : 1;
    const nextKey = shiftCalendarDayKey(anchorDayKey, delta);
    if (nextKey) {
      setAnchorDayKey(nextKey);
      setSelectedDayKey(nextKey);
      const year = Number(nextKey.slice(0, 4));
      const month = Number(nextKey.slice(5, 7)) - 1;
      if (Number.isFinite(year) && Number.isFinite(month)) {
        setViewYear(year);
        setViewMonthIndex(month);
      }
    }
  };

  const onSelectView = (nextView: OwnerCalendarViewMode) => {
    setView(nextView);
    if (nextView === 'day') {
      setAnchorDayKey(selectedDayKey);
    }
    if (nextView === 'month') {
      const year = Number(selectedDayKey.slice(0, 4));
      const month = Number(selectedDayKey.slice(5, 7)) - 1;
      if (Number.isFinite(year) && Number.isFinite(month)) {
        setViewYear(year);
        setViewMonthIndex(month);
      }
    }
  };

  const openDayView = useCallback((dayKey: string) => {
    setSelectedDayKey(dayKey);
    setAnchorDayKey(dayKey);
    setView('day');
  }, []);

  return (
    <section className={rootClass} aria-label={t('assets.ownerSchedule.title')}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>{t('assets.ownerSchedule.title')}</h3>
        <div className={styles.toolbar}>
          <div className={styles.viewToggle} role="group" aria-label={t('assets.ownerSchedule.viewMode')}>
            {(['day', 'week', 'month'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={[styles.viewButton, view === mode ? styles.viewButtonActive : '']
                  .filter(Boolean)
                  .join(' ')}
                aria-pressed={view === mode}
                onClick={() => onSelectView(mode)}
              >
                {t(
                  mode === 'day'
                    ? 'assets.ownerSchedule.viewDay'
                    : mode === 'week'
                      ? 'assets.ownerSchedule.viewWeek'
                      : 'assets.ownerSchedule.viewMonth',
                )}
              </button>
            ))}
          </div>
          <div className={styles.navGroup}>
            <button type="button" className={styles.navButton} onClick={onPrev} aria-label={t('assets.ownerSchedule.prev')}>
              ‹
            </button>
            <span className={styles.periodLabel}>{periodLabel}</span>
            <button type="button" className={styles.navButton} onClick={onNext} aria-label={t('assets.ownerSchedule.next')}>
              ›
            </button>
          </div>
        </div>
      </div>

      {loading ? <p className={styles.statusLine}>{t('common.loading')}</p> : null}
      {!loading && !error ? (
        <p className={styles.statusLine}>
          {t('assets.ownerSchedule.bookingCount', { n: bookings.length })}
        </p>
      ) : null}
      {error ? (
        <p className={styles.errorLine}>
          {t('assets.ownerSchedule.loadError')}
          {onRetry ? (
            <>
              {' '}
              <button type="button" className={styles.retryButton} onClick={onRetry}>
                {t('assets.retry')}
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {view === 'month' ? <CalendarLegend t={t} /> : null}

      {view === 'month' ? (
        <div className={styles.monthGrid}>
          {weekdays.map((label) => (
            <div key={label} className={styles.weekdayHead}>
              {label}
            </div>
          ))}
          {monthCellsToRender.map((cell) => {
            const count = monthCounts.get(cell.dayKey) ?? 0;
            const selected = cell.dayKey === selectedDayKey;
            const appearance = resolveOwnerCalendarDayAppearance(assetRecord, cell.dayKey, count);
            const cellClass = [
              styles.monthCell,
              !cell.inMonth ? styles.monthCellOutside : '',
              selected ? styles.monthCellSelected : '',
              APPEARANCE_CLASS[appearance],
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={cell.dayKey}
                type="button"
                className={cellClass}
                aria-pressed={selected}
                onClick={() => openDayView(cell.dayKey)}
              >
                <span className={styles.monthDayNumber}>{cell.day}</span>
                {count > 1 ? <span className={styles.bookingCount}>{count}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {view === 'week' ? (
        <div className={styles.weekGrid}>
          {weekdays.map((label) => (
            <div key={label} className={styles.weekdayHead}>
              {label}
            </div>
          ))}
          {weekKeys.map((dayKey) => (
            <WeekDayColumn
              key={dayKey}
              dayKey={dayKey}
              assetRecord={assetRecord}
              bookings={bookingsOnDay(bookings, dayKey)}
              language={language}
              t={t}
              onSelectDay={openDayView}
            />
          ))}
        </div>
      ) : null}

      {view === 'day' ? (
        <>
          <DayHourlyTimeline
            assetRecord={assetRecord}
            dayKey={anchorDayKey}
            bookings={bookingsOnDay(bookings, anchorDayKey)}
            language={language}
            t={t}
          />
          <BookingCards
            bookings={bookingsOnDay(bookings, anchorDayKey)}
            language={language}
            t={t}
            assetName={assetName}
            assetAddress={assetAddress}
          />
        </>
      ) : null}
    </section>
  );
}
